import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  BillingSystemCreateSchema,
  BillingSystemQueryAllSchema,
  BillingSystemQuerySchema,
  BillingSystemUpdateSchema,
} from './schemas';

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['BILLING_SYSTEMS'] });
};

export const useGetBillingSystemsMutation: UseControlledMutation<BillingSystemQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['BILLING_SYSTEMS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<BillingSystemQueryAllSchema>({
            endpoint: '/api/v1/billing-systems',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetBillingSystems(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['BILLING_SYSTEMS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<BillingSystemQueryAllSchema>({
        endpoint: '/api/v1/billing-systems',
        params: `${apiParamStr}&include=clientsCount`,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, BillingSystemCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/billing-systems',
        payload,
      }),
  });

export function useGetBillingSystem(id: string) {
  return useSuspenseQuery({
    queryKey: ['BILLING_SYSTEM', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: BillingSystemQuerySchema }>({
        endpoint: `/api/v1/billing-systems/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: BillingSystemQuerySchema }, ApiError, Partial<BillingSystemUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/billing-systems/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['BILLING_SYSTEM_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/billing-systems/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
